import { __awaiter, __decorate, __generator, __metadata } from "tslib";
import { Observable } from '../../Model/Decorators';
import OidcUser from './OidcUser';
import { SignInResult } from '../SignInResult';
import { Misc } from '../../';
import { NeoTypes } from '../../AppServices';
/**
 * Oidc authentication service base class.
 * You must override this class and provide the user manager implementation from the oidc library.
 */
var OidcAuthService = /** @class */ (function () {
    /**
     * Creates an Oidc auth service
     * @param userManager Oidc-client user manager instance
     * @param user User which existed in storage at startup
     */
    function OidcAuthService(userManager, axios) {
        this.userManager = userManager;
        this.axios = axios;
        this._user = null;
        this.userManager = userManager;
        this.userManager.events.addUserLoaded(this.onUserLoaded.bind(this));
        this.userManager.events.addUserUnloaded(this.onUserUnloaded.bind(this));
        if (!this.userManager.settings.automaticSilentRenew) {
            // If automaticSilentRenew is set to true, then the user manager will handle automatic renewal.
            this.userManager.events.addAccessTokenExpiring(this.signinSilent.bind(this));
        }
    }
    OidcAuthService.prototype.loadUserFromToken = function () {
        return this.loadUserFromSession();
    };
    OidcAuthService.prototype.loadUserFromSession = function () {
        return __awaiter(this, void 0, void 0, function () {
            var user;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.userManager.getUser()];
                    case 1:
                        user = _a.sent();
                        if (!user) return [3 /*break*/, 3];
                        // Get user does not raise the onUserLoaded event, so we call it manually.
                        this.onUserLoaded(user);
                        return [4 /*yield*/, this.completeAfterUserLoaded()];
                    case 2:
                        _a.sent();
                        _a.label = 3;
                    case 3: return [2 /*return*/, this.user];
                }
            });
        });
    };
    OidcAuthService.prototype.ensureSignedIn = function (redirectLocal) {
        var settings = this.userManager.settings;
        if (!this.isAuthenticated &&
            (!settings.redirect_uri || !window.location.href.startsWith(settings.redirect_uri)) &&
            (!settings.silent_redirect_uri || !window.location.href.startsWith(settings.silent_redirect_uri))) {
            this.beginSignIn(redirectLocal);
            return false;
        }
        return true;
    };
    /**
     * Redirect to identity server to get the auth token.
     * @param redirectPath Local path to redirect to once login is complete.
     */
    OidcAuthService.prototype.beginSignIn = function (redirectPath) {
        return this.userManager.signinRedirect({ state: redirectPath });
    };
    ;
    /**
     * Reads the access token from the query string and returns the redirect path.
     */
    OidcAuthService.prototype.endSignIn = function () {
        return __awaiter(this, void 0, void 0, function () {
            var user, e_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, this.userManager.signinRedirectCallback()];
                    case 1:
                        user = _a.sent();
                        return [2 /*return*/, Promise.resolve(user.state || "")];
                    case 2:
                        e_1 = _a.sent();
                        console.error(e_1);
                        return [2 /*return*/, Promise.reject(e_1)];
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Checks if the login state exists, and logs in the user.
     * Result.success will be false if an error occurs. Errors will be handled by the service bound to NeoTypes.Security.LoginErrorHandler.
     */
    OidcAuthService.prototype.tryCompleteSignIn = function () {
        return __awaiter(this, void 0, void 0, function () {
            var queryParams, stateKey, loginErrorHandler, redirectPath, e_2;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        queryParams = new URLSearchParams(window.location.search);
                        stateKey = queryParams.get("state");
                        loginErrorHandler = Misc.Globals.appService.get(NeoTypes.Security.LoginErrorHandler);
                        if (!(!stateKey || window.localStorage.getItem("oidc." + stateKey) === null)) return [3 /*break*/, 1];
                        loginErrorHandler.onMissingLoginState();
                        return [3 /*break*/, 5];
                    case 1:
                        _a.trys.push([1, 4, , 5]);
                        return [4 /*yield*/, this.endSignIn()];
                    case 2:
                        redirectPath = _a.sent();
                        return [4 /*yield*/, this.completeAfterUserLoaded()];
                    case 3:
                        _a.sent();
                        return [2 /*return*/, new SignInResult(true, redirectPath)];
                    case 4:
                        e_2 = _a.sent();
                        loginErrorHandler.onException(e_2);
                        return [3 /*break*/, 5];
                    case 5: return [2 /*return*/, new SignInResult(false, "")];
                }
            });
        });
    };
    OidcAuthService.prototype.endSignInSilent = function () {
        return this.userManager.signinSilentCallback();
    };
    OidcAuthService.prototype.onUserLoaded = function (user) {
        this.setUser(this.createUser(user));
        this.afterUserLoadedPromise = this.afterUserLoaded();
    };
    OidcAuthService.prototype.afterUserLoaded = function () {
    };
    OidcAuthService.prototype.completeAfterUserLoaded = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!this.afterUserLoadedPromise) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.afterUserLoadedPromise];
                    case 1:
                        _a.sent();
                        this.afterUserLoadedPromise = undefined;
                        _a.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        });
    };
    OidcAuthService.prototype.createUser = function (user) {
        return new OidcUser(user);
    };
    OidcAuthService.prototype.onUserUnloaded = function () {
        this.setUser(null);
    };
    OidcAuthService.prototype.setUser = function (user) {
        if (user) {
            this.axios.defaults.headers.common['Authorization'] = user.authKey;
        }
        else {
            delete this.axios.defaults.headers.common['Authorization'];
        }
        this._user = user;
    };
    OidcAuthService.prototype.signinSilent = function () {
        return this.userManager.signinSilent();
    };
    OidcAuthService.prototype.beginSignOut = function () {
        if (this._user) {
            this.userManager.signoutRedirect({ id_token_hint: this._user.userData.id_token });
            this.signOutLocal();
        }
        this.userManager.clearStaleState();
    };
    ;
    OidcAuthService.prototype.endSignOut = function () {
        return this.userManager.signoutRedirectCallback();
    };
    ;
    OidcAuthService.prototype.signOutLocal = function () {
        this.setUser(null);
    };
    Object.defineProperty(OidcAuthService.prototype, "user", {
        get: function () {
            return this._user;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(OidcAuthService.prototype, "isAuthenticated", {
        get: function () {
            return this.user !== null && !this.user.hasExpired;
        },
        enumerable: false,
        configurable: true
    });
    OidcAuthService.createIdentityServerMetadata = function (identityServerUrl) {
        return {
            issuer: identityServerUrl,
            jwks_uri: identityServerUrl + "/.well-known/openid-configuration/jwks",
            authorization_endpoint: identityServerUrl + "/connect/authorize",
            token_endpoint: identityServerUrl + "/connect/token",
            userinfo_endpoint: identityServerUrl + "/connect/userinfo",
            end_session_endpoint: identityServerUrl + "/connect/endsession",
            check_session_iframe: identityServerUrl + "/connect/checksession",
            revocation_endpoint: identityServerUrl + "/connect/revocation",
            introspection_endpoint: identityServerUrl + "/connect/introspect"
        };
    };
    /**
     * If this is a silent sign in (based on the current uri), `endSignInSilent()` is called on the oidc client, and this method returns true.
     * This should be called on startup of your app. If this returns true, don't continue with startup, since this iframe will be shortly disposed.
     * @param silentRedirectRoute The app relative path used for silent sign in callbacks.
     */
    OidcAuthService.isSilentSignIn = function (silentRedirectRoute) {
        var config = Misc.Globals.appService.get(NeoTypes.Config.ConfigModel);
        var silentRedirectUri = "".concat(window.location.origin).concat(config.baseUrl).concat(silentRedirectRoute);
        if (window.location.href.startsWith(silentRedirectUri)) {
            Misc.Globals.appService.get(NeoTypes.Security.AuthenticationService).endSignInSilent();
            return true;
        }
        return false;
    };
    __decorate([
        Observable(),
        __metadata("design:type", Object)
    ], OidcAuthService.prototype, "_user", void 0);
    return OidcAuthService;
}());
export default OidcAuthService;
